<template>
  <fragment>
    <div
      v-for="(componentData, index) of componentsData"
      :key="index"
      :is="componentData.name"
      v-bind="componentData.props"
      @chart-rendered="$emit('chart-rendered')"
      class="h-100 chart-div report-pdf-item"
    />
  </fragment>
</template>

<script>
import SurveyResultsChartPie from '@/components/chart_types/SurveyResultsChartPie';
import SurveyResultsChartTreemap from '@/components/chart_types/SurveyResultsChartTreemap';
import SurveyResultsChartLollipop from '@/components/chart_types/SurveyResultsChartLollipop';
import SurveyResultsChartBar from '@/components/chart_types/SurveyResultsChartBar';
import SurveyResultsChartHistogram from '@/components/chart_types/SurveyResultsChartHistogram';
import { CHART_TYPES, CHART_VALUE_TYPES, QUESTION_TYPES } from '@/store';

export default {
  name: 'ChartBuilder',
  props: {
    result: Object,
    format: {
      type: String, // 'dashboard' / 'results' / 'filters' / 'no-study-completed', 'job-question'
      required: true,
    },
    comparison: Object,
    isStatsVisible: {
      type: Boolean,
      default: true,
    },
    isTitleVisible: {
      type: Boolean,
      default: true,
    },
    isBorderVisible: {
      type: Boolean,
      default: true,
    },
    isWithReport: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SurveyResultsChartPie,
    SurveyResultsChartTreemap,
    SurveyResultsChartLollipop,
    SurveyResultsChartBar,
    SurveyResultsChartHistogram,
  },
  computed: {
    question: function () {
      if (this.result && this.result.question) {
        return this.result.question;
      }
      return null;
    },

    chartType: function () {
      if (
        this.question &&
        this.question.chart &&
        this.question.chart.chart_type
      ) {
        return this.question.chart.chart_type;
      }
      return null;
    },
    chartConfig: function () {
      if (
        this.question &&
        this.question.chart &&
        this.question.chart.chart_config
      ) {
        return this.question.chart.chart_config;
      }
      return null;
    },
    orientation: function () {
      if (this.chartConfig && this.chartConfig.orientation) {
        return this.chartConfig.orientation;
      }
      return null;
    },
    layout: function () {
      if (this.chartConfig && this.chartConfig.layout) {
        return this.chartConfig.layout;
      }
      return null;
    },
    filteredResults: function () {
      if (
        ['results', 'dashboard', 'no-study-completed', 'job-question'].includes(
          this.format
        )
      ) {
        return this.result.results;
      }
      return this.result.results.filter((r) => {
        if (r.results) {
          return r.results.filter((rr) =>
            this.comparison.charts.variableIds.includes(rr.variable_id)
          );
        }
        return this.comparison.charts.variableIds.includes(r.variable_id);
      });
    },
    componentsData: function () {
      switch (this.chartType) {
        case CHART_TYPES.LOLLIPOP:
          return this.filteredResults.map((res) => {
            return {
              name: SurveyResultsChartLollipop.name,
              props: {
                title: this.filteredResults.length === 1 ? '' : res.label,
                results: res.results,
                format: this.format,
                responsesCount: ((this.result.question_type == QUESTION_TYPES.SELECT_ONE) || (this.result.question_type == QUESTION_TYPES.SELECT_ANY)) ? res.N_count : res.responses_count,
                questionType: this.result.question_type,
                isPercentage: this.isPercentage,
                orientation: this.orientation,
                yTitle: this.getYTitle,
                variable: res.variable,
                responses: res.responses,
                stats: res.stats,
                question: this.question,
                isStatsVisible: this.isStatsVisible,
                isTitleVisible: this.isTitleVisible,
                isWithReport: this.isWithReport,
              },
            };
          });
        case CHART_TYPES.BAR:
          return this.filteredResults.map((res) => {
            return {
              name: SurveyResultsChartBar.name,
              props: {
                title: this.filteredResults.length === 1 ? '' : res.label,
                results: res.results,
                format: this.format,
                responsesCount: ((this.result.question_type == QUESTION_TYPES.SELECT_ONE) || (this.result.question_type == QUESTION_TYPES.SELECT_ANY)) ? res.N_count : res.responses_count,
                unknownResponsesCount: (this.result.question_type == QUESTION_TYPES.SLIDER) ? res.unknown_count : 0,
                questionType: this.result.question_type,
                isPercentage: this.isPercentage,
                orientation: this.orientation,
                yTitle: this.getYTitle,
                variable: res.variable,
                responses: res.responses,
                stats: res.stats,
                question: this.question,
                isStatsVisible: this.isStatsVisible,
                isTitleVisible: this.isTitleVisible,
                isBorderVisible: this.isBorderVisible,
                isWithReport: this.isWithReport,
              },
            };
          });
        case CHART_TYPES.HISTOGRAM:
          return this.filteredResults.map((res) => ({
            name: SurveyResultsChartHistogram.name,
            props: {
              title: this.getVariableChartTitle(
                this.question,
                res.variable
                  ? res.variable.input_label
                  : this.question.firstVariable.input_label
              ),
              variableTitle: res.variable
                ? res.variable.short_label || res.variable.input_label
                : res.label,
              question: this.question,
              responses: res.responses,
              stats: res.stats,
              format: this.format,
              withScatter: this.chartConfig && this.chartConfig.scatter,
              responsesCount: ((this.result.question_type == QUESTION_TYPES.SELECT_ONE) || (this.result.question_type == QUESTION_TYPES.SELECT_ANY)) ? res.N_count : res.responses_count,
              unknownResponsesCount: (this.result.question_type == QUESTION_TYPES.SLIDER) ? res.unknown_count : 0,
              isStatsVisible: this.isStatsVisible,
              isTitleVisible: this.isTitleVisible,
              isBorderVisible: this.isBorderVisible,
              isWithReport: this.isWithReport,
            },
          }));
        case CHART_TYPES.PIE:
          return this.filteredResults.map((res) => ({
            name: SurveyResultsChartPie.name,
            props: {
              title: this.filteredResults.length === 1 ? '' : res.label,
              results: res.results,
              questionType: this.result.question_type,
              format: this.format,
              responsesCount: ((this.result.question_type == QUESTION_TYPES.SELECT_ONE) || (this.result.question_type == QUESTION_TYPES.SELECT_ANY)) ? res.N_count : res.responses_count,
              isPercentage: this.isPercentage,
              isStatsVisible: this.isStatsVisible,
              isTitleVisible: this.isTitleVisible,
              isBorderVisible: this.isBorderVisible,
            },
          }));
        case CHART_TYPES.TREEMAP:
          return this.filteredResults.map((res) => {
            return {
              name: SurveyResultsChartTreemap.name,
              props: {
                title: this.filteredResults.length === 1 ? '' : res.label,
                results: res.results,
                format: this.format,
                responsesCount: ((this.result.question_type == QUESTION_TYPES.SELECT_ONE) || (this.result.question_type == QUESTION_TYPES.SELECT_ANY)) ? res.N_count : res.responses_count,
                questionType: this.result.question_type,
                layout: this.layout,
                isStatsVisible: this.isStatsVisible,
                isTitleVisible: this.isTitleVisible,
              },
            };
          });
        default:
          break;
      }
      switch (this.result.question_type) {
        case 'DERIVED_VARIABLE':
          return [
            {
              name: SurveyResultsChartPie.name,
              props: {
                title: '',
                results: this.result.results,
                questionType: QUESTION_TYPES.SELECT_ONE,
                format: this.format,
                responsesCount: this.result.responses_count,
                isPercentage: this.isPercentage,
                isStatsVisible: this.isStatsVisible,
                isTitleVisible: this.isTitleVisible,
                isBorderVisible: this.isBorderVisible,
              },
            },
          ];
        case QUESTION_TYPES.SELECT_ONE:
        case QUESTION_TYPES.SELECT_ANY:
        case QUESTION_TYPES.FIND_REGION:
        case QUESTION_TYPES.FIND_STATE:
        case QUESTION_TYPES.YES_NO:
        case QUESTION_TYPES.RATING:
          return this.filteredResults.map((res) => {
            return {
              name: SurveyResultsChartBar.name,
              props: {
                title: '',
                results: res.results,
                format: this.format,
                responsesCount: res.responses_count,
                questionType: this.result.question_type,
                isPercentage: this.isPercentage,
                orientation: this.orientation,
                yTitle: this.getYTitle,
                isStatsVisible: this.isStatsVisible,
                isTitleVisible: this.isTitleVisible,
                isBorderVisible: this.isBorderVisible,
              },
            };
          });
        case QUESTION_TYPES.SLIDER:
        case QUESTION_TYPES.CONFIRMATION:
          return this.filteredResults.map((res) => ({
            name: SurveyResultsChartHistogram.name,
            props: {
              title: this.getVariableChartTitle(
                this.result,
                res.variable.label
              ),
              question: this.question,
              responses: res.responses,
              stats: res.stats,
              format: this.format,
              withScatter: false,
              responsesCount: res.responses_count,
              isStatsVisible: this.isStatsVisible,
              isTitleVisible: this.isTitleVisible,
              isBorderVisible: this.isBorderVisible,
            },
          }));
      }
      return [];
    },
    isPercentage: function () {
      return (
        this.chartConfig &&
        this.chartConfig.value_type &&
        this.chartConfig.value_type === CHART_VALUE_TYPES.PERCENTAGE
      );
    },
    getYTitle: function () {
      return this.isPercentage ? 'Percentage' : 'Count';
    },
  },
  methods: {
    // when we're showing one chart per variable on a question,
    // only give the chart a title if there is > 1 variable
    getVariableChartTitle(q, varLabel) {
      if (q && q.question && q.question.variablesCount === 1) {
        return '';
      } else {
        return varLabel;
      }
    },
  },
};
</script>

<style scoped></style>
